import { SortTh } from '../../components/SortTh';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CONTACT_KIND_LABELS, CONTACT_KINDS, type Contact, type ContactKind, type Paginated } from '@kontor/shared';
import { api, queryString } from '../../api';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { useSession } from '../../session';
import { RelationBadges, useRelationTypes } from './relationTypes';

/** Liste der Kontakte mit Suche und Filter nach Art und Beziehungsart (KON-104). Suche, Filter und Seite stehen in der Adresszeile. */
export function ContactsPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const kind = (params.get('kind') ?? '') as ContactKind | '';
  const page = Number(params.get('page') ?? 1);
  const sort = params.get('sort') ?? 'name';
  const q = params.get('q') ?? '';
  const relationTypeId = params.get('beziehung') ?? '';
  const archived = params.get('archiv') ?? '';
  const relationTypes = useRelationTypes();

  const list = useQuery({
    queryKey: ['contacts', { kind, page, q, relationTypeId, sort, archived }],
    queryFn: () => api<Paginated<Contact>>('GET', `/contacts${queryString({ kind, page, q, relationTypeId, sort, archived: archived ? 'true' : '', perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };

  const onSort = (next: string) => update({ sort: next === 'name' ? '' : next });

  const fields: FilterField[] = [
    { key: 'archiv', label: 'Bestand', type: 'select', options: [{ value: '', label: 'Aktive' }, { value: 'true', label: 'Archiviert' }] },
    { key: 'kind', label: 'Art', type: 'select', options: [{ value: '', label: 'Alle' }, ...CONTACT_KINDS.map((k) => ({ value: k, label: CONTACT_KIND_LABELS[k] }))] },
    ...(relationTypes.list.length > 0
      ? [{ key: 'beziehung', label: 'Beziehung', type: 'select' as const, options: [{ value: '', label: 'Alle' }, ...relationTypes.list.map((t) => ({ value: t.id, label: t.name }))] }]
      : []),
  ];

  return (
    <>
      <div className="page-head">
        <h1>Kontakte</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
        <Link className="btn btn--secondary btn--sm page-head__action" to="/kontakte/dubletten">Dubletten prüfen</Link>
        {can('contacts.write') && <Link className="btn btn--primary btn--sm" to="/kontakte/neu">Neuer Kontakt</Link>}
      </div>

      <FilterBar
        search={{ value: q, placeholder: 'Name, E-Mail, Telefon oder Ort', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ kind, beziehung: relationTypeId, archiv: archived }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />

      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}

      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Name" sortKey="name" sort={sort} onSort={onSort} />
              <SortTh label="Art" sortKey="kind" sort={sort} onSort={onSort} />
              <th>Beziehung</th>
              <SortTh label="E-Mail" sortKey="email" sort={sort} onSort={onSort} />
              <SortTh label="Telefon" sortKey="phone" sort={sort} onSort={onSort} />
              <SortTh label="Ort" sortKey="city" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr>
                <td colSpan={6} className="muted">Wird geladen …</td>
              </tr>
            )}
            {list.data?.data.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  {archived ? 'Keine archivierten Kontakte.' : q || kind || relationTypeId ? 'Keine Kontakte zu dieser Suche.' : 'Noch keine Kontakte. Legen Sie den ersten über „Neuer Kontakt“ an.'}
                </td>
              </tr>
            )}
            {list.data?.data.map((c) => (
              <ExpandRow
                key={c.id}
                summary={
                  <Summary
                    title={<Link to={`/kontakte/${c.id}`} className="strong-link">{c.name}</Link>}
                    line2={[CONTACT_KIND_LABELS[c.kind], [c.postalCode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
                    line3={c.email ?? c.phone ?? undefined}
                  />
                }
              >
                <td className="cell-desktop-title">
                  <Link to={`/kontakte/${c.id}`} className="strong-link">{c.name}</Link>
                </td>
                <td data-label="Art">{CONTACT_KIND_LABELS[c.kind]}</td>
                <td data-label="Beziehung">{c.relationTypeIds.length > 0 ? <RelationBadges ids={c.relationTypeIds} /> : <span className="muted">–</span>}</td>
                <td data-label="E-Mail">{c.email ?? <span className="muted">–</span>}</td>
                <td data-label="Telefon" className="nowrap">{c.phone ?? <span className="muted">–</span>}</td>
                <td data-label="Ort">{[c.postalCode, c.city].filter(Boolean).join(' ') || <span className="muted">–</span>}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>

      {list.data && list.data.meta.lastPage > 1 && (
        <nav className="pager" aria-label="Seiten">
          <button className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </nav>
      )}
    </>
  );
}
